/**
 * Catálogos cerrados del formulario. Las claves (`valor`) son estables y son
 * lo que se guarda en la base; las etiquetas respetan la redacción del cliente
 * (Propuesta_encuesta_estudiantes2026_V2) y pueden cambiar sin migrar datos.
 */

export interface Opcion<V extends string = string> {
  valor: V
  etiqueta: string
  ayuda?: string
  /** Agregado por REVOLUT.TEAM (no estaba en la propuesta original). */
  nuevo?: boolean
}

export const NIVELES = [
  { valor: 'inicial', etiqueta: 'Educación inicial' },
  { valor: 'primaria', etiqueta: 'Educación primaria' },
  { valor: 'secundaria', etiqueta: 'Educación secundaria', ayuda: 'Media / liceo' },
  { valor: 'universitaria', etiqueta: 'Educación universitaria' },
  { valor: 'otro', etiqueta: 'Otro' },
] as const satisfies readonly Opcion[]
export type Nivel = (typeof NIVELES)[number]['valor']

/** Niveles en los que responde obligatoriamente el representante (D-005). */
export const NIVELES_CON_REPRESENTANTE: readonly Nivel[] = ['inicial', 'primaria']

export const RANGO_EDAD_POR_NIVEL: Record<Nivel, readonly [number, number] | null> = {
  inicial: [0, 6],
  primaria: [5, 14],
  secundaria: [10, 21],
  universitaria: [15, 70],
  otro: null,
}

export const LUGARES_INICIO = [
  { valor: 'vivienda_familiar', etiqueta: 'Vivienda de padres o familiares' },
  { valor: 'residencia_estudiantil', etiqueta: 'Residencia estudiantil' },
  { valor: 'vivienda_compartida', etiqueta: 'Vivienda compartida con otras personas' },
  { valor: 'otro', etiqueta: 'Otro' },
] as const satisfies readonly Opcion[]
export type LugarInicio = (typeof LUGARES_INICIO)[number]['valor']

export const MODOS = [
  { valor: 'autobus_publico', etiqueta: 'Autobús público', ayuda: 'Buseta, camionetica, por puesto' },
  { valor: 'autobus_institucion', etiqueta: 'Autobús de la institución educativa' },
  { valor: 'transporte_escolar', etiqueta: 'Transporte escolar contratado', nuevo: true },
  { valor: 'metro', etiqueta: 'Metro', ayuda: 'Incluye Metrocable y Cabletrén' },
  { valor: 'tren', etiqueta: 'Tren' },
  { valor: 'motocicleta', etiqueta: 'Motocicleta', ayuda: 'Propia o familiar' },
  { valor: 'mototaxi', etiqueta: 'Mototaxi', nuevo: true },
  { valor: 'automovil', etiqueta: 'Automóvil particular', ayuda: 'Propio, familiar o «cola»' },
  { valor: 'taxi', etiqueta: 'Taxi' },
  { valor: 'app', etiqueta: 'Servicio de transporte por aplicación', ayuda: 'Ridery, Yummy u otra' },
  { valor: 'bicicleta', etiqueta: 'Bicicleta' },
  { valor: 'a_pie', etiqueta: 'A pie' },
  { valor: 'otro', etiqueta: 'Otro' },
] as const satisfies readonly Opcion[]
export type Modo = (typeof MODOS)[number]['valor']

/** Modos que normalmente implican un pago por viaje. */
export const MODOS_PAGADOS: readonly Modo[] = ['taxi', 'app', 'mototaxi', 'autobus_publico', 'transporte_escolar']
/** Modos sin costo directo de pasaje. */
export const MODOS_SIN_COSTO: readonly Modo[] = ['a_pie', 'bicicleta']
export const MODOS_NO_MOTORIZADOS: readonly Modo[] = ['a_pie', 'bicicleta']

/** Velocidad máxima plausible (km/h, línea recta) por familia de modo. */
export const VELOCIDAD_MAXIMA: Record<'a_pie' | 'bicicleta' | 'motorizado', number> = {
  a_pie: 7,
  bicicleta: 25,
  motorizado: 80,
}

export const DIFICULTADES = [
  { valor: 'costo', etiqueta: 'Costo del transporte' },
  { valor: 'tiempo', etiqueta: 'Tiempo de viaje' },
  { valor: 'falta_transporte', etiqueta: 'Falta de transporte' },
  { valor: 'frecuencia', etiqueta: 'Frecuencia insuficiente' },
  { valor: 'horarios', etiqueta: 'Horarios' },
  { valor: 'distancia', etiqueta: 'Distancia' },
  { valor: 'seguridad', etiqueta: 'Seguridad' },
  { valor: 'congestion', etiqueta: 'Congestión vehicular' },
  { valor: 'vias', etiqueta: 'Condiciones de las vías' },
  { valor: 'otro', etiqueta: 'Otro' },
  { valor: 'ninguna', etiqueta: 'No tengo dificultades' },
] as const satisfies readonly Opcion[]
export type Dificultad = (typeof DIFICULTADES)[number]['valor']

export const RANGOS_DURACION = [
  { valor: 'lt15', etiqueta: 'Menos de 15 minutos', min: 0, max: 14 },
  { valor: '15_30', etiqueta: 'Entre 15 y 30 minutos', min: 15, max: 30 },
  { valor: '31_45', etiqueta: 'Entre 31 y 45 minutos', min: 31, max: 45 },
  { valor: '46_60', etiqueta: 'Entre 46 y 60 minutos', min: 46, max: 60 },
  { valor: '61_90', etiqueta: 'Entre 61 y 90 minutos', min: 61, max: 90 },
  { valor: 'gt90', etiqueta: 'Más de 90 minutos', min: 91, max: Number.POSITIVE_INFINITY },
] as const
export type RangoDuracion = (typeof RANGOS_DURACION)[number]['valor']

export const MONEDAS = [
  { valor: 'VES', etiqueta: 'Bs', ayuda: 'Bolívares' },
  { valor: 'USD', etiqueta: 'USD', ayuda: 'Dólares' },
] as const satisfies readonly Opcion[]
export type Moneda = (typeof MONEDAS)[number]['valor']

export const TARIFA_ESTUDIANTIL = [
  { valor: 'siempre', etiqueta: 'Sí, siempre' },
  { valor: 'a_veces', etiqueta: 'A veces' },
  { valor: 'no', etiqueta: 'No' },
  { valor: 'no_aplica', etiqueta: 'No aplica' },
] as const satisfies readonly Opcion[]
export type TarifaEstudiantil = (typeof TARIFA_ESTUDIANTIL)[number]['valor']

export const INFORMANTES = [
  { valor: 'estudiante', etiqueta: 'El estudiante' },
  { valor: 'representante', etiqueta: 'Su representante (madre, padre o responsable)' },
] as const satisfies readonly Opcion[]
export type Informante = (typeof INFORMANTES)[number]['valor']

export const PARENTESCOS = [
  { valor: 'madre', etiqueta: 'Madre' },
  { valor: 'padre', etiqueta: 'Padre' },
  { valor: 'abuelo', etiqueta: 'Abuela o abuelo' },
  { valor: 'tio', etiqueta: 'Tía o tío' },
  { valor: 'otro', etiqueta: 'Otro responsable' },
] as const satisfies readonly Opcion[]
export type Parentesco = (typeof PARENTESCOS)[number]['valor']

export const MODOS_CAPTURA = ['autoaplicada_qr', 'asistida'] as const
export type ModoCaptura = (typeof MODOS_CAPTURA)[number]

export const ROLES = ['superadmin', 'coordinador', 'supervisor', 'encuestador', 'analista'] as const
export type Rol = (typeof ROLES)[number]

export const ETIQUETA_ROL: Record<Rol, string> = {
  superadmin: 'Superadministrador',
  coordinador: 'Coordinador regional',
  supervisor: 'Supervisor de institución',
  encuestador: 'Encuestador',
  analista: 'Analista',
}

export function etiqueta<V extends string>(lista: readonly Opcion<V>[], valor: V | string | null | undefined): string {
  return lista.find((o) => o.valor === valor)?.etiqueta ?? (valor ?? '')
}

export function valores<V extends string>(lista: readonly { valor: V }[]): V[] {
  return lista.map((o) => o.valor)
}
